import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ShieldCheck,
  Trash2,
  Server,
  AlertTriangle,
  BarChart3,
  Store,
  Briefcase,
  ShoppingBag,
} from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { ChannelManager } from "@/pages/ChannelManager";
import { CardAuditLog } from "@/pages/CardAuditLog";
import { TranslatorProvidersCard } from "@/pages/admin/TranslatorProvidersCard";
import { SmtpConfigCard } from "@/pages/admin/SmtpConfigCard";
import { NotificationsAdminCard } from "@/pages/admin/NotificationsAdminCard";
import { AnalyticsConfigCard } from "@/pages/admin/AnalyticsConfigCard";
import { GoogleSignInConfigCard } from "@/pages/admin/GoogleSignInConfigCard";
import { FacebookLoginConfigCard } from "@/pages/admin/FacebookLoginConfigCard";
import { LinkedInSignInConfigCard } from "@/pages/admin/LinkedInSignInConfigCard";
import { PasskeyLoginConfigCard } from "@/pages/admin/PasskeyLoginConfigCard";
import { ChatbotConfigCard } from "@/pages/admin/ChatbotConfigCard";
import { PromoBannersCard } from "@/pages/admin/PromoBannersCard";
import { BrandLogosCard } from "@/pages/admin/BrandLogosCard";
import { LandingHeroCard } from "@/pages/admin/LandingHeroCard";
import { SiteBrandingCard } from "@/pages/admin/SiteBrandingCard";
import { FeaturedShelvesCard } from "@/pages/admin/FeaturedShelvesCard";
import { SibaScoreRangesCard } from "@/pages/admin/SibaScoreRangesCard";
import { SibaScoreCacheCard } from "@/pages/admin/SibaScoreCacheCard";
import { ScoreOfWeekCard } from "@/pages/admin/ScoreOfWeekCard";
import { SignalWeightsCard } from "@/pages/admin/SignalWeightsCard";
import { AffiliateProvidersCard } from "@/pages/admin/AffiliateProvidersCard";
import { TrashBinPanel } from "@/pages/admin/TrashBinPanel";
import { PaymentProvidersCard } from "@/pages/admin/PaymentProvidersCard";
import { MarketingProvidersCard } from "@/pages/admin/MarketingProvidersCard";
import { CurrencyFxCard } from "@/pages/admin/CurrencyFxCard";
import { SyncLimitsCard } from "@/pages/admin/SyncLimitsCard";
import { PshbSubscriptionsCard } from "@/pages/admin/PshbSubscriptionsCard";
import { HealthCard } from "@/pages/admin/HealthCard";
import { ApiErrorsCard } from "@/pages/admin/ApiErrorsCard";
import { AutoSyncStatusCard } from "@/pages/admin/AutoSyncStatusCard";
import { AICartConfigCard } from "@/pages/admin/AICartConfigCard";
import { AICartAnalyticsCard } from "@/pages/admin/AICartAnalyticsCard";
import { ShopSampleImagesCard } from "@/pages/admin/ShopSampleImagesCard";
import { FcmConfigCard } from "@/pages/admin/FcmConfigCard";
import { MarketingRibbonCard } from "@/pages/admin/MarketingRibbonCard";
import { DemoLoginCard } from "@/pages/admin/DemoLoginCard";
import { DataResetCard } from "@/pages/admin/DataResetCard";
import { TwilioCostTrackerCard } from "@/pages/admin/TwilioCostTrackerCard";

// Tabs -----
// Three thematic umbrella tabs (Shop / Workspace / AI Cart) group every
// tunable knob by which shopper-facing module it drives. Four operations
// tabs stay separate (they aren't shopper-visible: system health, API
// errors, analytics telemetry, trash bin).
type AdminTab = "shop" | "workspace" | "ai-cart" | "system" | "errors" | "analytics" | "trash";

const TABS: Array<{ id: AdminTab; label: string; icon: any; testId: string }> = [
  { id: "shop",       label: "Shop Page",       icon: Store,        testId: "admin-tab-shop" },
  { id: "workspace",  label: "Workspace",       icon: Briefcase,    testId: "admin-tab-workspace" },
  { id: "ai-cart",    label: "AI Cart™",         icon: ShoppingBag,  testId: "admin-tab-ai-cart" },
  { id: "system",     label: "System",          icon: Server,       testId: "admin-tab-system" },
  { id: "errors",     label: "API Errors",      icon: AlertTriangle, testId: "admin-tab-errors" },
  { id: "analytics",  label: "Analytics",       icon: BarChart3,    testId: "admin-tab-analytics" },
  { id: "trash",      label: "Trash Bin",       icon: Trash2,       testId: "admin-tab-trash" },
];

// Legacy tab-id aliases so old bookmarks keep working after the reshuffle.
// Each legacy tab now maps to the closest new umbrella tab.
const LEGACY_TAB_MAP: Record<string, AdminTab> = {
  oauth: "workspace",
  payments: "shop",
  notifications: "workspace",
  promos: "shop",
  hero: "shop",
  sibascore: "shop",
  signals: "shop",
  brand: "shop",
};

export const AdminSetup: React.FC = () => {
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "shop";
  // Redirect legacy tab ids to their new umbrella tab so /admin/setup?tab=oauth
  // still lands somewhere useful.
  const initialTab: AdminTab = (
    TABS.some((t) => t.id === rawTab) ? (rawTab as AdminTab) :
    (LEGACY_TAB_MAP[rawTab] || "shop")
  );
  const [tab, setTab] = useState<AdminTab>(initialTab);
  const goTab = (id: AdminTab) => {
    setTab(id);
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set("tab", id);
      return next;
    });
  };

  return (
    <DashboardLayout
      title="Admin Setup"
      subtitle="Every configurable knob for SociBaba, grouped by which shopper-facing module it drives — Shop, Workspace, AI Cart™."
      seoPath="/admin/setup"
      seoDescription="Super-admin configuration console for the SociBaba Shop, workspace tools, and the AI Cart™ module."
    >
      {readonly && (
        <div className="mb-4 sg-card p-4 flex items-start gap-3 border-l-4 border-l-blue-500 bg-blue-50/60" data-testid="readonly-banner">
          <ShieldCheck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-800 leading-relaxed">
            <div className="font-semibold mb-0.5">Read-only mode (Staff)</div>
            You can view configuration and diagnostics but cannot edit credentials or purge data. Ask an admin to make changes.
          </div>
        </div>
      )}

      {/* Tab nav — 7 top-level tabs. First three are shopper-facing module
           umbrellas (Shop / Workspace / AI Cart), last four are ops-only. */}
      <div className="mb-6 border-b border-[color:var(--sg-border)] pb-0" data-testid="admin-tabs">
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-x-1 gap-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={t.testId}
                onClick={() => goTab(t.id)}
                className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  active
                    ? "border-[color:var(--sg-violet)] text-[color:var(--sg-fg)]"
                    : "border-transparent text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {tab === "shop" && (
      <div data-testid="admin-tab-panel-shop" className="space-y-6">
        <TabIntro
          title="Shop Page Configuration"
          subtitle="Every setting that shoppers on /shop see — hero, promos, product scores, payments, currency."
        />
        <LandingHeroCard />
        <SiteBrandingCard />
        <ShopSampleImagesCard />
        <PromoBannersCard />
        <FeaturedShelvesCard />
        <BrandLogosCard />
        <SibaScoreRangesCard />
        <SibaScoreCacheCard />
        <ScoreOfWeekCard />
        <SignalWeightsCard />
        <PaymentProvidersCard />
        <CurrencyFxCard />
        <AffiliateProvidersCard />
      </div>
      )}

      {tab === "workspace" && (
      <div data-testid="admin-tab-panel-workspace" className="space-y-6">
        <TabIntro
          title="Workspace Configuration"
          subtitle="Sign-in providers, notifications, translations, marketing — everything sellers and creators touch inside the app."
        />
        <DemoLoginCard />
        <ChatbotConfigCard />
        <MarketingRibbonCard />
        <FcmConfigCard />
        <TwilioCostTrackerCard />
        <GoogleSignInConfigCard />
        <FacebookLoginConfigCard />
        <LinkedInSignInConfigCard />
        <PasskeyLoginConfigCard />
        <ChannelManager />
        <NotificationsAdminCard />
        <MarketingProvidersCard />
        <TranslatorProvidersCard />
        <div className="rounded-xl border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 p-4 text-sm">
          <div className="font-semibold text-[color:var(--sg-fg)] mb-1">How users connect</div>
          <p className="text-[color:var(--sg-muted)]">
            Once you save credentials for a channel (or the channel uses per-user App Passwords / Bot Tokens),
            every SociBaba user sees the standard Google / Facebook / provider consent popup on the{" "}
            <a href="/platforms" className="underline">Platforms</a> page.
          </p>
        </div>
      </div>
      )}

      {tab === "ai-cart" && (
      <div data-testid="admin-tab-panel-ai-cart" className="space-y-6">
        <TabIntro
          title="AI Cart™ Configuration"
          subtitle="Every knob for the /ai-cart module — VIP tiers, Cheer Wall, credits, marketplace whitelist, share TTLs."
        />
        <AICartAnalyticsCard />
        <AICartConfigCard />
      </div>
      )}

      {tab === "system" && (
      <div data-testid="admin-tab-panel-system" className="space-y-4">
        <TabIntro
          title="System Operations"
          subtitle="Backend health, sync limits, SMTP, and PSHB subscriptions. Not shopper-facing."
        />
        <HealthCard />
        <AutoSyncStatusCard />
        <SyncLimitsCard />
        <SmtpConfigCard />
        <PshbSubscriptionsCard />
        <DataResetCard />
      </div>
      )}

      {tab === "errors" && (
      <div data-testid="admin-tab-panel-errors" className="space-y-4">
        <TabIntro title="API Errors & Audit Log" subtitle="Recent 5xx errors and admin-action audit trail." />
        <ApiErrorsCard />
        <CardAuditLog />
      </div>
      )}

      {tab === "analytics" && (
      <div data-testid="admin-tab-panel-analytics" className="space-y-4">
        <TabIntro title="Analytics & Telemetry" subtitle="Ecommerce funnel, Meta Pixel, GA4 configuration." />
        <AnalyticsConfigCard />
      </div>
      )}

      {tab === "trash" && (
      <div data-testid="admin-tab-panel-trash" className="space-y-4">
        <TabIntro title="Trash Bin" subtitle="Restore or permanently delete soft-deleted items." />
        <TrashBinPanel />
      </div>
      )}
    </DashboardLayout>
  );
};

const TabIntro: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <div className="pb-1">
    <h2 className="font-display font-bold text-xl tracking-tight text-slate-900 dark:text-white">{title}</h2>
    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>
  </div>
);

export default AdminSetup;
